/**
 * Promise dedup for web-component loads, keyed by custom-element tag name.
 *
 * The browser's `customElements` registry is the authoritative source of
 * truth, but it only flips from "undefined" to "defined" after a
 * `<script type="module">` fetch + eval + `customElements.define()` round-trip
 * — easily 100s of ms cold. The window between "we decided to load tag T"
 * and "registry sees T" lets concurrent callers race into a parallel load:
 * duplicate API requests, duplicate `<script>` tags, and a
 * `NotSupportedError: this name has already been registered` throw on the
 * losing side of the `define()` race.
 *
 * This tracker holds an in-flight promise per tag so all callers — `$W
 * .loadByTagName(t)`, the global-autoload pipeline, the
 * `<script type="wippy-components-loader">` DOM scanner — converge on the
 * same load attempt whenever any of them is mid-flight for the same tag.
 *
 * Failures evict from the tracker so a subsequent call can retry the load.
 * Successes stay tracked; the `customElements.get(tag)` short-circuit at
 * the top of `trackLoad` makes future calls O(1) without consulting the
 * map (the browser registry is now authoritative). Keeping the entry is
 * a microscopic memory cost and makes the contract simpler: "any tag the
 * tracker has seen successfully load resolves to the same shared promise."
 */

const inflight = new Map<string, Promise<void>>()

/**
 * Returns a shared promise for loading `tag`. If `tag` is already defined
 * on `customElements`, returns a resolved promise without invoking `factory`.
 * If a load for `tag` is in-flight, returns the existing promise — `factory`
 * is NOT called a second time. Otherwise calls `factory()`, stores its
 * promise, and returns it.
 *
 * The factory's returned promise must resolve once the tag is defined (or
 * the load has provably failed). On rejection the tag is evicted so retries
 * fire a fresh factory.
 */
export function trackLoad(tag: string, factory: () => Promise<void>): Promise<void> {
  if (customElements.get(tag))
    return Promise.resolve()
  const existing = inflight.get(tag)
  if (existing)
    return existing
  const promise = factory().catch((error) => {
    inflight.delete(tag)
    throw error
  })
  inflight.set(tag, promise)
  return promise
}

/**
 * Default deadline for `whenDefinedWithTimeout`. Long enough to cover a
 * cold-cache CDN fetch + module evaluation on a slow network, short enough
 * to surface a permanently broken WC (script 404, syntax error, missing
 * `customElements.define` call) as a rejection instead of an indefinite
 * hang. Override per-call via the `timeoutMs` argument when a faster
 * failure is acceptable.
 */
export const DEFAULT_WHEN_DEFINED_TIMEOUT_MS = 30_000

/**
 * `customElements.whenDefined(tag)` with a hard deadline. Native
 * `whenDefined` has no rejection path — a script that fetches OK but
 * never calls `define` (404, parse error, dev forgot the `define` call)
 * leaves the promise pending forever, which would brick the tracker
 * entry: future callers for the same tag would inherit the hung promise.
 *
 * Resolves to `void` on successful registration. Rejects with a timeout
 * error after `timeoutMs` (default `DEFAULT_WHEN_DEFINED_TIMEOUT_MS`),
 * letting the tracker's catch evict and any retry re-fire fresh.
 *
 * The timer is cleared whichever way the race settles, so a successful
 * registration doesn't leak a pending `setTimeout`.
 */
export function whenDefinedWithTimeout(tag: string, timeoutMs?: number): Promise<void> {
  const ms = timeoutMs ?? DEFAULT_WHEN_DEFINED_TIMEOUT_MS
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  return Promise.race([
    customElements.whenDefined(tag).then(() => undefined),
    new Promise<never>((_resolve, reject) => {
      timeoutId = setTimeout(
        () => reject(new Error(`customElements.whenDefined("${tag}") timed out after ${ms}ms`)),
        ms,
      )
    }),
  ]).finally(() => {
    if (timeoutId !== undefined)
      clearTimeout(timeoutId)
  })
}

/**
 * Test-only: reset the tracker between unit tests. Production code should
 * never call this — successes are intentionally retained.
 *
 * @internal
 */
export function _resetTrackerForTests(): void {
  inflight.clear()
}
